import { useCallback, useEffect, useState } from 'react'
import {
  ActivityIndicator,
  Image,
  Linking,
  ScrollView,
  StyleSheet,
} from 'react-native'

import { BottomSheetScrollView } from '@gorhom/bottom-sheet'
import { useFocusEffect, useRoute } from '@react-navigation/native'
import { useQueryClient } from '@tanstack/react-query'
import LazyHOC from '@zet/common/components/lazyHoc'
import BrandDetailsLoader from '@zet/common/components/loader/brandDetailsLoader'
import { PressableWithIntent } from '@zet/common/components/pressableWithIntent'
import VideoBanner from '@zet/common/components/videoBanner'
import { useNavigation } from '@zet/common/hooks/useNavigation'
import { MY_TEAMS_ADVISOR } from '@zet/constants'
import { useFetchCustomerDetails } from '@zet/customer/src/api/hooks'
import useMyCustomersStore from '@zet/customer/src/store/myCustomers'
import { BottomSheet, colors as zetColors } from '@zet/design-system'
import { Button } from '@zet/design-system/button'
import { Icon } from '@zet/design-system/icons'
import { Stack, XStack, YStack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import {
  B2,
  B3,
  B4,
  B6,
  H5,
  SH1,
  SH2,
  SH3,
} from '@zet/design-system/typography'
import { APP_NAME } from '@zet/end-points'
import { useExperiments } from '@zet/experiments'
import {
  useBrandDetailsById,
  useFilteredRecommendationForPartner,
  useGenerateSharableLink,
  useGetLoyaltyTierDetails,
} from '@zet/feature-brands/api/brands/hooks'
import { useCreditCardCrossSelling } from '@zet/feature-brands/api/crossSelling/hooks'
import ChangePinCode from '@zet/feature-brands/components/changePincode'
import EmiCalculator from '@zet/feature-brands/components/emiCalculator'
import { useLoyaltyPayoutMeta } from '@zet/feature-loyalty'
import { useLoyaltyPayout } from '@zet/feature-loyalty/api/common/hooks/useLoyaltyPayout'
import { useTier } from '@zet/feature-loyalty/api/hooks'
import { i18n } from '@zet/localization'
import OnHold from '@zet/svg/onHold'
import RegistrationForm from '@zet/svg/registrationForm'
import RegistrationFormCheck from '@zet/svg/registrationFormCheck'
import { getCategoryName } from '@zet/utils'
import {
  BUTTON_TYPE,
  DEVICE_HEIGHT,
  DEVICE_WIDTH,
  rejectionPayoutCategoryList,
  zetStoreDpandaIds,
} from '@zet/utils/Constants'
import { getName } from '@zet/utils/stringConversion'

import WhiteStars from '../../assets/svg/whiteStars.svg'
import BrandHighlightV2 from '../../components/BrandHighlightV2'
import DetailPayoutWithCoins from '../../components/detailPayoutWithCoins'
import HighestPayouBottomsheet from '../../components/highestPayoutBottomsheet'
import HighestPayoutLoyaltyCard from '../../components/highestPayoutLoyaltyCard'
import { PayoutTable } from '../../components/payoutTable'
import useVariantDetails from '../../hooks/useVariantDetails'
import useBrandStore from '../../store/brands'
import {
  PROFIT_ON_ACTIVATION_BRAND_IDS,
  REASON_LIST,
} from '../../utils/constants'
import BrandTermsCondition from './brandTermsCondition'
import BrandTile from './brandTile'
import BrandTiles from './brandTiles'
import CreditCardCrossSellingBanner from './creditCardCrossSelling/creditCardCrossSellingBanner'

const BrandDetailsV2 = () => {
  const [isBottomSheetVisible, setIsBottomSheetVisible] =
    useState<boolean>(false)
  const [
    shareCardLinkBottomSheetVisible,
    setIsShareCardLinkBottomSheetVisible,
  ] = useState<boolean>(false)
  const [bottomSheetType, setBottomSheetType] = useState<string>('')
  const [
    isHighestPayoutBottomsheetVisible,
    setIsHighestPayoutBottomsheetVisible,
  ] = useState(false)
  const { params } = useRoute<any>()
  const { navigate } = useNavigation()
  const navigation = useNavigation()

  const { data: experiment } = useExperiments()
  const { data: tierData } = useGetLoyaltyTierDetails()
  const { data: variantData } = useVariantDetails({
    variantId: params?.variantId,
  })

  const isLongJourney = variantData?.isLongJourney

  const { isLinkShared, leadId, setLeadId, setIsLinkShared, setLeadSource } =
    useBrandStore((state) => state)
  const { setIsBrandsFlow, setPartnerId } = useMyCustomersStore(
    (state) => state
  )

  const brandId = params?.brandId
  const { data: getTier } = useTier()

  const { data: brandsData, isLoading } = useFilteredRecommendationForPartner({
    customerId: params?.customerId,
    partnerId: brandId,
  })

  const { data: creditCardCrossSelling } = useCreditCardCrossSelling(
    params?.customerId,
    brandId
  )

  const routeName = params?.customerId ? 'forCustomerFlow' : 'category'
  const dynamicRejectionPayout =
    brandsData?.eligiblePartners?.dynamicRejectionPayout
  const approvalRate = brandsData?.eligiblePartners?.approvalRate
  const approvalRateTextColor =
    brandsData?.eligiblePartners?.approvalRateTextColor

  const leadSource = params?.leadSource

  const isProfitOnActivationBrand = PROFIT_ON_ACTIVATION_BRAND_IDS.includes(
    String(brandId)
  )

  const queryClient = useQueryClient()
  // const fetch Varient data with varient id
  const { data: content } = useBrandDetailsById(brandId || '')
  const brandName = variantData?.name || content?.name || ''
  const brandCategory = content?.subCategory || ''
  const rejectionAmount = content?.rejectionAmount
  // const { data: partnerIds } = usePartnerIdsForBrand(content?.categoryId)
  const { data: customerData } = useFetchCustomerDetails(params?.customerId)

  const payoutCommission = variantData?.payout?.amount
    ? `${variantData?.payout?.amount} ${
        variantData?.payout?.type === 'percentage' ? '%' : ''
      }`
    : content?.commission

  const { handleCoinsColumn, handleExtraPayoutColumn, loyaltyPayoutError } =
    useLoyaltyPayoutMeta({
      payoutContent: payoutCommission,
      partnerId: brandId,
      isUserCurrentTierPayout: true,
      categoryId: content?.categoryId,
    })
  const { data: loyaltyPayout } = useLoyaltyPayout({
    partnerId: brandId,
    isHighestLoyaltyPayout: true,
    isUserCurrentTierPayout: true,
    isAllLevelPayout: true,
  })

  const isPartnerOnHold = variantData?.isPartnerOnHold

  const { mutate: shareLinkMutation, data: shareLinkData } =
    useGenerateSharableLink()

  const getColor = () => {
    switch (approvalRateTextColor) {
      case 'HIGH':
        return {
          text: 'text-success-200',
          bgColor: 'bg-success-10',
          iconColor: zetColors.success[150],
        }
      case 'MEDIUM':
        return {
          text: 'text-warning-200',
          bgColor: 'bg-warning-10',
          iconColor: zetColors.warning[150],
        }
      case 'LOW':
      default:
        return {
          text: 'text-error-200',
          bgColor: 'bg-error-10',
          iconColor: zetColors.error[150],
        }
    }
  }
  const infoColor = getColor()
  const applyForCustomer = () => {
    Linking.openURL(shareLinkData?.url)
  }

  const BrandFillApplicationLoadingPage = () => {
    navigate('BrandFillApplicationLoadingPage', {
      brandName,
      brandId,
      customerId: params?.customerId,
      customerName: getName(customerData?.fullName),
      leadSource: 'Assisted_lead_recommendation',
      isEligibleBrand: brandsData?.value,
    })
  }

  const isRejectionPayout = () => {
    if (routeName !== 'category') {
      return !(
        brandsData?.value &&
        rejectionPayoutCategoryList.includes(content?.categoryId?.toString()) &&
        dynamicRejectionPayout !== 0
      )
    } else {
      return !rejectionAmount
    }
  }

  function handleNavigationToTermsAndCondition() {
    navigate('BrandTermsConditions', {
      isEligible: brandsData?.value,
      brandId: params?.brandId,
      variantId: params?.variantId,
      partnerCategoryId: variantData?.partnerCategoryId,
      amount: dynamicRejectionPayout,
      routeName: routeName,
      baseAmount:
        typeof payoutCommission === 'string'
          ? parseFloat(payoutCommission?.replace(/[^0-9.]/g, ''))
          : payoutCommission,
    })
  }

  const onPressApplyButton = (_leadSource) => {
    shareLinkMutation({
      partnerId: brandId,
      customerId: params?.customerId,
      reopen: params?.reopen,
      leadSource: _leadSource || leadSource || 'app',
      isEligibleBrand: params?.isEligibleBrand,
    })
  }

  const handlePress = () => {
    setLeadId(null)
    queryClient.invalidateQueries({
      queryKey: [`globalShareLink-${brandId}`],
    })
    queryClient.invalidateQueries({
      queryKey: [`shareLink-${brandId}`],
    })
    setLeadSource(isLongJourney ? 'plWeb' : leadSource ?? 'app')
    navigate('ShareCardLink', {
      brandId: brandId,
      source: 'sellNow',
      customerName: getName(customerData?.fullName),
      customerPhoneNumber: customerData?.phoneNumber,
      customerId: routeName !== 'category' ? params?.customerId : null,
      isEligibleBrand: brandsData?.value, //true in case of Eligible and false in case of inEligible
      variantId: params?.variantId,
      eventName: 'click_action',
      brandCategory,
      eventPayload: {
        item_name: params?.customerId
          ? 'brands_list_recommendation_share'
          : 'brands_list_share',
        brand_name: brandName,
        brand_category: brandCategory,
        customer_id: params?.customerId,
        index: params?.index,
      },
      isDisclaimerVisible:
        content?.categoryId === 15 && APP_NAME !== MY_TEAMS_ADVISOR,
    })
  }

  const SellButton = ({
    btnText,
    onPressApplyButton,
  }: {
    btnText?: string
    onPressApplyButton?: () => void
  }) => (
    <PressableWithIntent
      type={BUTTON_TYPE.BUTTON}
      size={'medium'}
      name={'SellNow'}
      className="w-full flex-1 justify-center"
      onPress={onPressApplyButton || handlePress}
      eventName="click_action"
      eventPayload={{
        item_name: customerData?.fullName
          ? 'brands_list_recommendation_sell'
          : 'brands_list_sell',
        brand_name: brandName,
        brand_category: brandCategory,
        cta_name: customerData?.fullName
          ? `${i18n.t('brandDetails.buttontext', {
              category: brandCategory,
            })} ${i18n.t('brandDetails.to')} ${customerData?.fullName}`
          : i18n.t('brandDetails.buttontext', {
              category: brandCategory,
            }),
      }}
    >
      {btnText ? (
        btnText
      ) : (
        <>
          {i18n.t('brandDetails.sellButtonText', {
            category: getCategoryName(content?.categoryId),
            name: getName(customerData?.fullName),
          })}
        </>
      )}
    </PressableWithIntent>
  )

  const getBottomView = (showButton: boolean, isHold = false) => {
    if (brandId === zetStoreDpandaIds.partnerId && !isHold) {
      return (
        <Stack className="border-grays-30 h-[75] border-t bg-white p-2">
          <SellButton btnText={i18n.t('shareCardLink.sellNow')} />
        </Stack>
      )
    }
    if (isHold) {
      return (
        <Stack className="border-grays-30 h-[75] border-t bg-white p-2">
          <Pressable className="bg-grays-30 active:bg-grays-10 w-full rounded-md">
            <XStack className="items-center justify-center space-x-2 rounded py-3">
              <OnHold />
              <SH3 className="text-grays-80">{i18n.t('brandPage.onHold')}</SH3>
            </XStack>
          </Pressable>
        </Stack>
      )
    } else if (isLongJourney && routeName !== 'category') {
      return (
        <Stack className="border-grays-30 h-[150] border-t bg-white p-3">
          <Button
            size={'medium'}
            textStyle="text-[#fff]"
            className="active:bg-primary-10 mb-2 w-full py-3"
            onPress={() => {
              handlePress()
            }}
          >
            {i18n.t('brandDetails.shareButtonLink', {
              category: getCategoryName(content?.categoryId),
              name: getName(customerData?.fullName),
            })}
          </Button>
          <PressableWithIntent
            type={BUTTON_TYPE.BUTTON}
            outlined
            size={'medium'}
            className={'w-full flex-1 justify-center'}
            textStyle="text-primary-100"
            onPress={() => {
              onPressApplyButton('plApp')
            }}
            eventName="click_action"
            eventPayload={{
              item_name: 'brands_list_add_customer',
              brand_name: brandName,
              brand_category: brandCategory,
            }}
          >
            {i18n.t('brandDetails.startButtonText', {
              category: getCategoryName(content?.categoryId),
              name: getName(customerData?.fullName),
            })}
          </PressableWithIntent>
        </Stack>
      )
    } else if (
      !isHold &&
      !showButton &&
      params?.customerId &&
      APP_NAME !== MY_TEAMS_ADVISOR
    ) {
      if (
        !REASON_LIST.includes(brandsData?.inEligiblePartners?.criteria | '')
      ) {
        if (experiment?.[`UI_ASSISTED_JOURNEY_${brandId}`]) {
          return (
            <XStack className="border-grays-30 justify-between space-x-2 border-t bg-white px-3 py-2">
              <Button
                outlined
                size={'medium'}
                className={'mr-2 flex-1 justify-center'}
                textStyle="text-primary-100"
                onPress={BrandFillApplicationLoadingPage}
                eventName="click_action"
                eventPayload={{
                  item_name: 'brands_list_fill_application',
                  brand_name: brandName,
                  brand_category: brandCategory,
                }}
              >
                {i18n.t('brandDetails.fillApplication')}
              </Button>
              <SellButton btnText={i18n.t('brandDetails.shareLink')} />
            </XStack>
          )
        } else {
          return (
            <Stack>
              {creditCardCrossSelling?.partner &&
              experiment?.UI_isCreditCardCrossSelling ? (
                <Stack className="border-grays-30 -mb-2">
                  <CreditCardCrossSellingBanner
                    crossSellingData={creditCardCrossSelling}
                    customerName={customerData?.fullName}
                    customerId={params?.customerId}
                    customerPhoneNumber={customerData?.phoneNumber}
                    tier={getTier?.cohort}
                    extraPayoutPercentage={getTier?.extraPayoutPercentage}
                  />
                </Stack>
              ) : (
                <></>
              )}
              <Stack
                className="border-grays-30 h-[75] bg-white p-2"
                style={styles.roundedBorder}
              >
                <SellButton />
              </Stack>
            </Stack>
          )
        }
      } else {
        return <></>
      }
    } else if (!isHold && !showButton && params?.customerId) {
      if (
        brandsData?.inEligiblePartners?.criteria !== 'LEAD_EXISTS' &&
        APP_NAME !== MY_TEAMS_ADVISOR
      ) {
        return (
          <Stack className="border-grays-30 h-[75] border-t bg-white p-2">
            <SellButton />
          </Stack>
        )
      } else if (APP_NAME === MY_TEAMS_ADVISOR) {
        return (
          <XStack className="border-grays-30 justify-between space-x-2 border-t bg-white px-3 py-2">
            <Button
              outlined
              size={'medium'}
              className={'mr-2 flex-1'}
              textStyle="text-primary-100"
              onPress={handlePress}
              eventName="click_action"
              eventPayload={{
                item_name: 'brands_list_add_customer',
                brand_name: brandName,
                brand_category: brandCategory,
              }}
            >
              {i18n.t('myTeams.shareLinkNow')}
            </Button>
            <SellButton
              btnText={i18n.t('brandPage.applyNow')}
              onPressApplyButton={onPressApplyButton}
            />
          </XStack>
        )
      } else {
        return <></>
      }
    } else if (params?.customerId || routeName !== 'category') {
      return (
        <XStack className="border-grays-30 h-[75] items-center justify-between border-t bg-white p-2 px-3">
          <XStack className="w-[41%] items-center">
            <Icon type="LinkOutlined" />
            <B4>{i18n.t('brandDetails.linkSharedText')}</B4>
          </XStack>
          {routeName !== 'category' && (
            <Stack className="w-[50%]">
              <Button
                size={'medium'}
                outlined={true}
                textStyle="text-primary-100"
                className="active:bg-primary-10 w-full px-0"
                onPress={() => {
                  navigate('LeadProgress', { leadId })
                }}
              >
                {i18n.t('brandDetails.checkLeadStatus')}
              </Button>
            </Stack>
          )}
        </XStack>
      )
    } else {
      return (
        <XStack className="border-grays-30 justify-between space-x-2 border-t bg-white px-3 py-2">
          <PressableWithIntent
            type={BUTTON_TYPE.BUTTON}
            outlined
            name={'AddCustomer'}
            size={'medium'}
            className={'mr-2 flex-1'}
            textStyle="text-primary-100"
            onPress={() => {
              setIsBrandsFlow(true)
              setPartnerId(brandId)
              navigate('KnowYourCustomer', { source: 'brand' })
            }}
            eventName="click_action"
            eventPayload={{
              item_name: 'brands_list_add_customer',
              brand_name: brandName,
              brand_category: brandCategory,
            }}
          >
            {i18n.t('myCustomers.add')}
          </PressableWithIntent>
          <SellButton btnText={i18n.t('shareCardLink.sellNow')} />
        </XStack>
      )
    }
  }

  useEffect(() => {
    return () => {
      setIsLinkShared(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useFocusEffect(
    useCallback(() => {
      setPartnerId(null)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
  )
  useEffect(() => {
    if (shareLinkData) {
      if (shareLinkData?.statusCode === 3309) {
        setBottomSheetType('notEligible')
        setIsShareCardLinkBottomSheetVisible(true)
      } else if (
        (shareLinkData?.url && shareLinkData?.statusCode === 3307) ||
        (shareLinkData?.url && shareLinkData?.statusCode === 3308)
      ) {
        setBottomSheetType('missingDetails')
        setIsShareCardLinkBottomSheetVisible(true)
      } else if (shareLinkData?.status === 409) {
        setBottomSheetType('alreadyApplied')
        setIsShareCardLinkBottomSheetVisible(true)
      } else if (shareLinkData?.statusCode === 3323) {
        setBottomSheetType('generic')
        setIsShareCardLinkBottomSheetVisible(true)
      } else if (shareLinkData?.url) {
        applyForCustomer(shareLinkData?.url)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareLinkData])

  if (!content) {
    return <ActivityIndicator />
  }

  return (
    <>
      <ScrollView
        className="flex-1 bg-white"
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={1}
      >
        <Stack>
          {APP_NAME === 'zet-app' && (
            <>
              <VideoBanner
                image={content?.applicationProcess?.thumbnail}
                videoData={content?.applicationProcess?.videoContent}
                videoId={content?.applicationProcess?.videoLink}
                isPressable={true}
                eventName="click_action"
                imageStyle={{ borderRadius: 0, height: 148 }}
                imageClasses={'h-[148px]'}
                resizeMode={'cover'}
                eventPayload={{
                  item_name: 'brands_list_banner',
                  brand_name: brandName,
                  brand_category: brandCategory,
                }}
              />
            </>
          )}

          {!!content?.logo && (
            <XStack className="items-center justify-between">
              <Image
                source={{ uri: content?.logo }}
                resizeMode="contain"
                className="mx-3 my-1"
                style={{ width: 120, height: 60 }}
              />
              {params?.isPreApprovedPartner && (
                <Stack className="items-center justify-center">
                  <Image
                    source={require('../../assets/images/strip.png')}
                    style={styles.bannerStrip}
                    resizeMode="contain"
                  />

                  <XStack className="mr-3 items-center justify-center">
                    <WhiteStars height={20} width={20} />
                    <B4 className="ml-1 text-white">
                      {i18n.t('brandDetails.preQualified')}
                    </B4>
                  </XStack>
                </Stack>
              )}
            </XStack>
          )}
        </Stack>

        <Stack className="relative -top-[15] h-full w-full rounded-t-lg bg-white">
          {variantData?.name && (
            <H5 className="mt-4/2 mx-3">{variantData?.name}</H5>
          )}

          {routeName !== 'category' && isLoading ? (
            <BrandDetailsLoader />
          ) : (
            <LazyHOC>
              {/* {loansCategoriesEmiCalculator.includes(
                content?.categoryId?.toString()
              ) && (
                <Stack className="mt-3 pl-3">
                  <BrandHighlight highlights={content?.topFeatures} />
                </Stack>
              )} */}

              <Stack className="mb-3.5 mt-3 pl-3">
                <BrandHighlightV2
                  topBenefits={variantData?.topBenefitsBrandDetail}
                />
              </Stack>
              {routeName === 'category' && (
                <Stack className="border-grays-5 border-t-4">
                  <ChangePinCode
                    categoryId={content?.categoryId}
                    brandId={brandId}
                    isDetailsFlow
                    eventName="click_action"
                    eventPayload={{
                      item_name: 'brands_list_change_pincode',
                      brand_name: brandName,
                      brand_category: brandCategory,
                    }}
                  />
                </Stack>
              )}
              {routeName === 'category' && (
                <BrandTile brandId={brandId} isHold={isPartnerOnHold} />
              )}
              <YStack>
                {routeName !== 'category' &&
                  brandsData?.value &&
                  approvalRate && (
                    <XStack
                      className={`flex-1 items-center space-x-1 px-3 pt-3 ${infoColor.bgColor}`}
                    >
                      <Icon type="SecureOutlined" color={infoColor.iconColor} />
                      <SH2 className={`${infoColor.text} rounded`}>
                        {i18n.t('recommendationCard.approvalChance', {
                          initialText: approvalRate,
                        })}
                      </SH2>
                    </XStack>
                  )}

                {routeName !== 'category' &&
                  brandsData?.value &&
                  customerData?.fullName && (
                    <Stack className={`pb-2 pl-2 pr-3 ${infoColor.bgColor}`}>
                      <B3
                        className={`text-success-200 rounded px-3 py-2 ${infoColor.text}`}
                      >
                        {customerData?.fullName?.toUpperCase()}
                        {i18n.t('brandDetails.heading.details')}
                        <SH2>{variantData?.name || content?.name}</SH2>
                      </B3>
                    </Stack>
                  )}

                {routeName !== 'category' &&
                  !brandsData?.value &&
                  brandsData?.inEligiblePartners?.reason && (
                    <Stack className="border-grays-5 mt-3 border-t-4">
                      <B3 className="text-grays-80 bg-error-10 rounded p-3">
                        <B2 className="text-error-200">
                          {brandsData?.inEligiblePartners?.reason}
                        </B2>
                      </B3>
                    </Stack>
                  )}

                {/* no need for zet coins or bonus here  */}
                {experiment?.UI_HighestPayoutFlow &&
                loyaltyPayout?.isLoyaltyPayoutApplicable
                  ? APP_NAME !== MY_TEAMS_ADVISOR && (
                      <Stack className="border-grays-5 border-t-4">
                        {!handleCoinsColumn() && !handleExtraPayoutColumn() ? (
                          <Pressable
                            className="mx-3 mt-2"
                            onPress={handleNavigationToTermsAndCondition}
                          >
                            <XStack className="mb-2 items-center">
                              <Stack className="grow">
                                <DetailPayoutWithCoins
                                  commission={payoutCommission}
                                  categoryId={content?.categoryId}
                                  partnerId={brandId || content?.id}
                                />
                                <B2 className="text-grays-80 mt-[1]">
                                  {isProfitOnActivationBrand
                                    ? i18n.t(
                                        'brandDetails.profitTextOnActivation'
                                      )
                                    : i18n.t('brandDetails.profitText')}
                                </B2>
                              </Stack>
                              <Stack>
                                <Icon
                                  type="ArrowRight"
                                  className="text-grays-80 -mr-2"
                                />
                              </Stack>
                            </XStack>

                            <Stack className="my-2">
                              <B6 className="text-grays-60">
                                <B6 className="text-error-100">*</B6>
                                {i18n.t('brandDetails.termsCondition')}
                              </B6>
                            </Stack>
                          </Pressable>
                        ) : loyaltyPayoutError ? (
                          <></>
                        ) : content?.categoryId != 20 ? (
                          <Stack>
                            <SH3 className="text-grays-80 my-2 ml-3">
                              {isProfitOnActivationBrand
                                ? i18n.t('brandDetails.profitTextOnActivation')
                                : i18n.t('brandDetails.profitText')}
                            </SH3>
                            <HighestPayoutLoyaltyCard
                              setIsBottomSheetVisible={
                                setIsHighestPayoutBottomsheetVisible
                              }
                              tier={getTier}
                              tierData={tierData}
                              content={content}
                            />
                            {!handleCoinsColumn() &&
                            !handleExtraPayoutColumn() ? (
                              <></>
                            ) : (
                              <PayoutTable
                                categoryId={content?.categoryId}
                                // commission={content?.commission}
                                commission={payoutCommission}
                                tier={getTier}
                                tierData={tierData}
                                partnerId={brandId || content?.id}
                                partnerCategoryId={
                                  variantData?.partnerCategoryId
                                }
                              />
                            )}
                            {/* <RejectionPayout
                              isShowRejectionPayout={isRejectionPayout()}
                              amount={dynamicRejectionPayout}
                              routeName={routeName}
                            /> */}
                            <Pressable
                              className="items-center py-3"
                              onPress={handleNavigationToTermsAndCondition}
                              eventName="click_action"
                              eventPayload={{
                                item_name: 'brands_list_brand_payout',
                                brand_name: brandName,
                                brand_category: brandCategory,
                              }}
                            >
                              <SH3 className="text-primary-100">
                                {i18n.t('brandDetails.viewPayoutTerms')}
                              </SH3>
                            </Pressable>
                          </Stack>
                        ) : (
                          <></>
                        )}
                      </Stack>
                    )
                  : APP_NAME !== MY_TEAMS_ADVISOR && (
                      <Pressable
                        className="border-grays-5 border-t-4 px-3 pt-4"
                        onPress={() =>
                          navigate('BrandTermsConditions', {
                            isEligible: brandsData?.value,
                            brandId: params?.brandId,
                            variantId: params?.variantId,
                            amount: dynamicRejectionPayout,
                            routeName: routeName,
                          })
                        }
                        eventName="click_action"
                        eventPayload={{
                          item_name: 'brands_list_brand_payout',
                          brand_name: brandName,
                          brand_category: brandCategory,
                        }}
                      >
                        <XStack className="items-center">
                          <Stack className="ml-2 grow">
                            <DetailPayoutWithCoins
                              commission={payoutCommission}
                              categoryId={content?.categoryId}
                              partnerId={brandId || content?.id}
                            />
                            <B2 className="text-grays-80 mt-[1]">
                              {/* TODO:// Pick from brandcomm */}
                              {isProfitOnActivationBrand
                                ? i18n.t('brandDetails.profitTextOnActivation')
                                : i18n.t('brandDetails.profitText')}
                            </B2>
                          </Stack>
                          <Stack>
                            <Icon
                              type="ArrowRight"
                              className="text-grays-80 -mr-2"
                            />
                          </Stack>
                        </XStack>
                        {/* {APP_NAME !== MY_TEAMS_ADVISOR && (
                          <RejectionPayout
                            isShowRejectionPayout={isRejectionPayout()}
                            amount={dynamicRejectionPayout}
                            routeName={routeName}
                          />
                        )} */}
                        <Stack className="my-2">
                          <B6 className="text-grays-60">
                            <B6 className="text-error-100">*</B6>
                            {i18n.t('brandDetails.termsCondition')}
                          </B6>
                        </Stack>
                      </Pressable>
                    )}
              </YStack>
              <BrandTiles brandId={brandId} variantId={params?.variantId} />
              {APP_NAME !== MY_TEAMS_ADVISOR && (
                <BrandTermsCondition brandId={brandId} />
              )}
            </LazyHOC>
          )}
        </Stack>
      </ScrollView>
      {routeName !== 'category' && isLoading ? (
        <></>
      ) : (
        <LazyHOC>
          {REASON_LIST.includes(
            brandsData?.inEligiblePartners?.criteria || ''
          ) ? (
            <></>
          ) : (
            getBottomView(isLinkShared, isPartnerOnHold)
          )}
          <BottomSheet
            visible={isBottomSheetVisible}
            disableBottomSheet={() => setIsBottomSheetVisible(false)}
            onDismiss={() => setIsBottomSheetVisible(false)}
            sheetName="brand_emi_calculater"
            // backgroundColor={zetColors.tertiary[150]}
          >
            <>
              <XStack
                className="items-center justify-between px-3 py-3"
                style={{ backgroundColor: zetColors.tertiary[150] }}
              >
                <XStack className="items-center space-x-3">
                  <Icon type="Keypad" color={zetColors.neutral.white100} />
                  <SH1 className="text-white">
                    {i18n.t('calculater.calculator_heading')}
                  </SH1>
                </XStack>
                <Pressable onPress={() => setIsBottomSheetVisible(false)}>
                  <Icon type="Close" color={zetColors.neutral.white100} />
                </Pressable>
              </XStack>
              <BottomSheetScrollView
                style={{
                  maxHeight: DEVICE_HEIGHT - 200,
                }}
              >
                <ScrollView
                  showsVerticalScrollIndicator={false}
                  style={{ flex: 1 }}
                >
                  <EmiCalculator />
                </ScrollView>
              </BottomSheetScrollView>
            </>
          </BottomSheet>
          <BottomSheet
            visible={shareCardLinkBottomSheetVisible}
            disableBottomSheet={() =>
              setIsShareCardLinkBottomSheetVisible(false)
            }
            onDismiss={() => setIsShareCardLinkBottomSheetVisible(false)}
            sheetName="share_card_link"
          >
            <>
              <Stack className="mb-3 mt-3 self-center">
                {bottomSheetType !== 'generic' ? (
                  <RegistrationForm />
                ) : (
                  <RegistrationFormCheck />
                )}
              </Stack>
              <B2 className="mx-3 mb-3 text-center">
                {bottomSheetType === 'missingDetails'
                  ? i18n.t('shareCardLink.missingDetails')
                  : bottomSheetType === 'alreadyApplied'
                  ? i18n.t('shareCardLink.alreadyApplied')
                  : shareLinkData?.message}
              </B2>
              <Stack className="border-grays-30 border-t px-3 py-3">
                {bottomSheetType === 'missingDetails' && (
                  <Button
                    className="w-full"
                    size="medium"
                    onPress={() => {
                      setIsShareCardLinkBottomSheetVisible(false)
                      navigation.navigate('WebViewScreen', {
                        url: shareLinkData?.url,
                      })
                    }}
                  >
                    {i18n.t('shareCardLink.fill')}
                  </Button>
                )}
                {bottomSheetType === 'notEligible' ||
                bottomSheetType === 'alreadyApplied' ||
                bottomSheetType === 'generic' ? (
                  <Button
                    className="w-full"
                    size="medium"
                    onPress={() => setIsShareCardLinkBottomSheetVisible(false)}
                  >
                    {i18n.t('shareCardLink.okay')}
                  </Button>
                ) : (
                  <></>
                )}
              </Stack>
            </>
          </BottomSheet>

          {isHighestPayoutBottomsheetVisible && tierData && (
            <HighestPayouBottomsheet
              isBottomSheetVisible={isHighestPayoutBottomsheetVisible}
              setIsBottomSheetVisible={setIsHighestPayoutBottomsheetVisible}
              categoryId={content?.categoryId}
              commission={payoutCommission}
              brandName={variantData?.name || content?.name}
              tier={getTier}
              tierData={tierData}
              partnerId={brandId || content?.id}
              partnerCategoryId={variantData?.partnerCategoryId}
            />
          )}
        </LazyHOC>
      )}
    </>
  )
}

export default BrandDetailsV2

const styles = StyleSheet.create({
  roundedBorder: {
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
  cardHeight: { height: DEVICE_HEIGHT * 0.4, width: DEVICE_WIDTH * 0.8 },
  bannerStrip: {
    position: 'absolute',
    width: 130,
    height: 40,
    right: 0,
  },
})
